import { SelectionModel } from '@angular/cdk/collections';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  combineLatest,
  finalize,
  lastValueFrom,
  map,
  Observable,
  startWith,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { type AdditionalOutflowsModel } from 'src/app/models/additional-outflows.model';
import { type FoodItemOutflowModel } from 'src/app/models/food-item-outflow.model';
import {
  UnidadMedida,
  type FoodItemModel,
} from 'src/app/models/food-item.model';
import { AdditionalOutflowsService } from 'src/app/services/api/additional-outflows.service';
import { AyBService } from 'src/app/services/api/ayb.service';
import { PdfGeneratorService } from 'src/app/services/pdf-generator.service';
import { NotificationService } from 'src/shared/notification.service';
import Swal from 'sweetalert2';
import { DialogEditOutflowObservationComponent } from '../dialog-edit-outflow-observation/dialog-edit-outflow-observation.component';
import { DialogFoodOutflowEvidenceComponent } from '../dialog-evidence/dialog-evidence.component';
import { type GroupedFoodItemOutflow } from '../models';

@Component({
  selector: 'app-dialog-additional-outflow',
  templateUrl: './dialog-additional-outflow.component.html',
  styleUrls: ['./dialog-additional-outflow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogAdditionalOutflowComponent implements OnInit, OnDestroy {
  private _unsubscribeOnDestroy$: Subject<boolean> = new Subject<boolean>();
  activeTextArea = -1;
  outflowQuantity = 0;
  isLoading = true;
  isLoadingGenerarPdf = false;
  isEditingAdditionalOutflowObservation = false;
  additionalOutflowObservation = '';
  displayForm = false;
  newFoodItemForm;

  additionalOutflowFoodItems: FoodItemOutflowModel[] = [];
  warehouseFoodItems: FoodItemModel[] = [];
  availableCategories: string[] = [];
  selectedCategoria: string | null = null;

  selection = new SelectionModel<FoodItemOutflowModel>(true, []);

  foodItemsAutocomplete$: Observable<FoodItemModel[]> = new Observable<
    FoodItemModel[]
  >();

  dataSource: MatTableDataSource<FoodItemOutflowModel> =
    new MatTableDataSource();

  displayedColumns = [
    'select',
    'codigoInsumo', // Displayed as Categoria
    'nombre',
    'cantidad', // Displayed as Cantidad Retirada
    'unidadMedida', // Displayed as Unidad de Medida
    'observation', // Displayed as Observación
    'evidencia',
    'delete-outflow',
  ];

  @ViewChild('autosize') autosize!: CdkTextareaAutosize;

  paginator!: MatPaginator;
  sort!: MatSort;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.dataSource.paginator = this.paginator;
  }
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.dataSource.sort = this.sort;
  }
  set setDisplayForm(value: boolean) {
    this.displayForm = value;
  }

  constructor(
    public dialogRef: MatDialogRef<DialogAdditionalOutflowComponent>,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: { additionalOutflow: AdditionalOutflowsModel },
    public matDialogHandler: MatDialog,
    private fb: FormBuilder,
    private _ngZone: NgZone,
    private readonly aybService: AyBService,
    private readonly additionalOutflowsService: AdditionalOutflowsService,
    private readonly pdfGeneratorService: PdfGeneratorService,
    private readonly notificationService: NotificationService,
    private readonly cdRef: ChangeDetectorRef,
  ) {
    this.newFoodItemForm = this.fb.group({
      foodItem: new FormControl<FoodItemModel | null>(
        null,
        Validators.required,
      ),
      cantidad: new FormControl<number>(0, [
        Validators.required,
        Validators.min(1),
        Validators.pattern(/^\d+$/),
      ]),
      unidadMedida: new FormControl<UnidadMedida | null>(
        null,
        Validators.required,
      ),
      observation: new FormControl<string>('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(500),
      ]),
    });
  }
  ngOnInit(): void {
    this.isLoading = true;
    this.additionalOutflowObservation =
      this.dialogData.additionalOutflow.observation || '';

    combineLatest({
      warehouseFoodItems: this.aybService.queryGetAllFoodItemsSku().pipe(
        takeUntil(this._unsubscribeOnDestroy$),
        map((response) => response.getAllFoodItemsSku?.insumos || []),
      ),
      outflows: this.aybService
        .queryGetAllEgresosByAdditionalOutflowId({
          additionalOutflowId: this.dialogData.additionalOutflow.id || '',
        })
        .pipe(
          takeUntil(this._unsubscribeOnDestroy$),
          map((response) => response.getAllEgresosByAdditionalOutflowId),
        ),
    })
      .pipe(
        takeUntil(this._unsubscribeOnDestroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.warehouseFoodItems = response.warehouseFoodItems;

          this.additionalOutflowFoodItems = response.outflows.map((outflow) => {
            const warehouseItem = response.warehouseFoodItems.find(
              (item) => item.id === outflow.insumoId,
            );
            return {
              ...outflow,
              codigoInsumo: warehouseItem?.codigoInsumo,
            };
          });

          this.dataSource.data = this.additionalOutflowFoodItems;

          this._extractAvailableCategories();

          this.foodItemsAutocomplete$ =
            this.newFoodItemForm.controls.foodItem.valueChanges.pipe(
              startWith(''),
              map((value) => {
                const nombreInsumo =
                  typeof value === 'string' ? value : value?.nombreInsumo;
                return nombreInsumo
                  ? this._autocompleteFilterWarehouseFoodItems(nombreInsumo)
                  : this.warehouseFoodItems.slice();
              }),
            );
        },
        error: (error) => {
          console.error('Error fetching data', error);
          this.notificationService.notifyError(
            'Error al cargar los datos de los insumos y egresos.',
          );
        },
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeOnDestroy$.next(true);
    this._unsubscribeOnDestroy$.complete();
  }

  private _autocompleteFilterWarehouseFoodItems(nombreInsumo: string) {
    const filterValue = nombreInsumo.toLowerCase();
    return this.warehouseFoodItems.filter(
      (foodItem) =>
        foodItem.nombreInsumo?.toLocaleLowerCase().includes(filterValue) &&
        this.additionalOutflowFoodItems.every(
          (outflow) => outflow.insumoId !== foodItem.id,
        ),
    );
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected == numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.data.forEach((row) => this.selection.select(row));
    }
  }

  triggerResize() {
    this._ngZone.onStable
      .pipe(take(1))
      .subscribe(() => this.autosize.resizeToFitContent(true));
  }
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  applyFilterSelect() {
    let dataNew = this.additionalOutflowFoodItems;

    if (this.selectedCategoria) {
      dataNew = dataNew.filter((outflow) => {
        const codigoInsumo = (outflow as any).codigoInsumo || '';
        return codigoInsumo.startsWith(this.selectedCategoria!);
      });
    }

    this.dataSource.data = dataNew;
  }

  addFoodItemToAdditionalOutflow() {
    if (
      this.newFoodItemForm.value.foodItem &&
      this.newFoodItemForm.value.unidadMedida &&
      this.newFoodItemForm.value.cantidad &&
      this.newFoodItemForm.value.observation
    ) {
      this.aybService
        .mutationAddFoodItemToAdditionalOutflow({
          additionalOutflowId: this.dialogData.additionalOutflow.id || '',
          foodItemId: this.newFoodItemForm.value.foodItem.id || '',
          quantity: this.newFoodItemForm.value.cantidad || 0,
          observation: this.newFoodItemForm.value.observation || '',
        })
        .pipe(
          takeUntil(this._unsubscribeOnDestroy$),
          map((res) => res.addFoodItemToAdditionalOutflow),
        )
        .subscribe({
          next: (addFoodItemToAdditionalOutflow) => {
            this.additionalOutflowFoodItems = [
              ...this.additionalOutflowFoodItems,
              addFoodItemToAdditionalOutflow,
            ];
            this.dataSource.data = this.additionalOutflowFoodItems;
            this.newFoodItemForm.reset();
          },
          error: () => {
            this.notificationService.notifyError(
              'Error al agregar el insumo al egreso adicional.',
            );
          },
        });
    } else {
      this.notificationService.notifyWarning(
        'Falta ingresar campos requeridos',
      );
    }
  }

  updateOutflowQuantity(outflow: FoodItemOutflowModel) {
    this.activeTextArea = -1;
    if (outflow.cantidad === this.outflowQuantity) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        title: 'Misma cantidad. No se realizaron cambios.',
        icon: 'info',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    this.aybService
      .mutationUpdateStockOutflowQuantity({
        egresoInsumoId: outflow.id || '',
        quantity: this.outflowQuantity,
      })
      .pipe(
        takeUntil(this._unsubscribeOnDestroy$),
        map((res) => res.updateEgresoInsumoQuantity),
      )
      .subscribe({
        next: (updatedOutflow) => {
          const updatedOutflowIndex = this.dataSource.data.findIndex(
            (outflowItem) => outflowItem.id === updatedOutflow.id,
          );
          this.dataSource.data[updatedOutflowIndex].cantidad =
            updatedOutflow.cantidad;
          this.notificationService.showSuccessAlert(
            'Cantidad actualizada exitosamente',
          );
          this.outflowQuantity = 0;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.notificationService.notifyError(
            'Error al actualizar la cantidad del egreso.',
          );
        },
      });
  }

  deleteOutflow(outflow: FoodItemOutflowModel) {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        this.aybService
          .mutationDeleteEgresoInsumo({
            outflowId: outflow.id || '',
          })
          .pipe(
            takeUntil(this._unsubscribeOnDestroy$),
            map((res) => res.mutationDeleteFoodOutflow),
          )
          .subscribe({
            next: () => {
              this.additionalOutflowFoodItems =
                this.additionalOutflowFoodItems.filter(
                  (item) => item.id !== outflow.id,
                );
              this.dataSource.data = this.additionalOutflowFoodItems;
              this.notificationService.showSuccessAlert(
                'Egreso eliminado exitosamente',
              );
            },
            error: (error) => {
              console.error('Error deleting outflow:', error);
              this.notificationService.notifyError(
                'Error al eliminar el egreso',
              );
            },
          });
      }
    });
  }

  setActiveTextArea(i: number, outflow: FoodItemOutflowModel) {
    this.outflowQuantity = outflow.cantidad || 0;
    this.activeTextArea = i;
    this.cdRef.detectChanges();
  }

  openDialogEditObservation(outflow: FoodItemOutflowModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      prevObservation: outflow.observation,
      egresoInsumo: outflow,
    };

    dialogConfig.width = '390px';
    dialogConfig.minHeight = '226px';
    dialogConfig.autoFocus = false;
    const dialogRef = this.matDialogHandler.open(
      DialogEditOutflowObservationComponent,
      dialogConfig,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.editedOutflow) {
        return;
      }
      const updatedEgreso = result.editedOutflow;
      const index = this.additionalOutflowFoodItems.findIndex(
        (item) => item.id === updatedEgreso.id,
      );

      if (index !== -1) {
        this.additionalOutflowFoodItems[index] = updatedEgreso;
        this.dataSource.data = this.additionalOutflowFoodItems;
        this.notificationService.showSuccessAlert(
          'Observación actualizada exitosamente',
        );
      }
    });
  }

  openDialogUploadEvidence(outflow: FoodItemOutflowModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      outflow,
    };
    let dialogRef = this.matDialogHandler.open(
      DialogFoodOutflowEvidenceComponent,
      dialogConfig,
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((result: { updatedOutflow: FoodItemOutflowModel }) => {
        if (
          !result ||
          !result?.updatedOutflow ||
          outflow.hasDocument === result.updatedOutflow.hasDocument
        ) {
          return;
        }

        const updatedOutflow = result.updatedOutflow;
        const index = this.additionalOutflowFoodItems.findIndex(
          (item) => item.id === updatedOutflow.id,
        );

        if (index === -1) {
          return;
        }
        this.additionalOutflowFoodItems[index] = updatedOutflow;
        this.dataSource.data = this.additionalOutflowFoodItems;
      });
  }

  getOptionText(option: any) {
    if (option && option.nombreInsumo) {
      return option.nombreInsumo;
    } else {
      return '';
    }
  }

  onFoodItemChanged(event: MatAutocompleteSelectedEvent) {
    this.newFoodItemForm
      .get(['unidadMedida'])
      ?.setValue(event.option.value.unidadMedida);
  }

  toggleFoodItemCheck(foodItem: GroupedFoodItemOutflow) {
    foodItem.checked = !foodItem.checked;
  }

  async generarPDF() {
    this.isLoadingGenerarPdf = true;
    const foodItemsAndCosts = await Promise.all(
      this.selection.selected.map(async (outflow) => {
        try {
          const res = await lastValueFrom(
            this.aybService.queryGetInsumoCosto({
              insumoId: outflow.insumoId || '',
            }),
          );
          const costo = res.getInsumoCosto;
          const costoTotal =
            Number(costo) *
            Number(outflow.cantidad === 0 ? 1 : outflow.cantidad);

          return {
            ...outflow,
            cantidadTotal: outflow.cantidad,
            costo,
            costoTotal: Number.isInteger(costoTotal)
              ? costoTotal
              : parseFloat(costoTotal.toFixed(1)),
          };
        } catch (error) {
          console.error(
            `Error obteniendo costo para insumo ${outflow.id}`,
            error,
          );
          return {
            ...outflow,
            costo: 0,
            costoTotal: 0,
          };
        }
      }),
    );

    try {
      await this.pdfGeneratorService.generatePdf({
        content: foodItemsAndCosts,
        headers: [
          'Insumo',
          'Unidad de Medida',
          'Cantidad Total',
          'Costo U.',
          'Costo Total',
        ],
        metadata: {
          productName: '-',
          cocineros: [],
          serviceId: '-',
          serviceName: '-',
          totalMenuXFilaAYB: 0,
          menu: '-',
        },
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
    }

    this.isLoadingGenerarPdf = false;
    this.cdRef.markForCheck();
  }

  enableAdditionalOutflowObservationEdit() {
    this.isEditingAdditionalOutflowObservation = true;
    this.cdRef.markForCheck();
  }

  cancelAdditionalOutflowObservationEdit() {
    this.isEditingAdditionalOutflowObservation = false;
    this.additionalOutflowObservation =
      this.dialogData.additionalOutflow.observation || '';
    this.cdRef.markForCheck();
  }

  saveAdditionalOutflowObservation() {
    if (!this.additionalOutflowObservation) {
      this.notificationService.notifyError(
        'La observación no puede estar vacía',
      );
      return;
    }

    if (
      this.additionalOutflowObservation ===
      this.dialogData.additionalOutflow.observation
    ) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        title: 'No se realizaron cambios en la observación',
        icon: 'info',
        showConfirmButton: false,
        timer: 3000,
      });
      this.isEditingAdditionalOutflowObservation = false;
      this.cdRef.markForCheck();
      return;
    }

    this.additionalOutflowsService
      .update(this.dialogData.additionalOutflow.id || '', {
        observation: this.additionalOutflowObservation,
      })
      .pipe(
        takeUntil(this._unsubscribeOnDestroy$),
        map((res) => res.updateAdditionalOutflow),
        finalize(() => {
          this.cdRef.markForCheck();
        }),
      )
      .subscribe({
        next: (updatedOutflow) => {
          this.dialogData.additionalOutflow.observation =
            updatedOutflow.observation;
          this.isEditingAdditionalOutflowObservation = false;
          this.notificationService.showSuccessAlert(
            'Observación actualizada exitosamente',
          );
        },
        error: (error) => {
          console.error(
            'Error updating additional outflow observation:',
            error,
          );
          this.notificationService.notifyError(
            'Error al actualizar la observación',
          );
          this.additionalOutflowObservation =
            this.dialogData.additionalOutflow.observation || '';
        },
      });
  }

  private _extractAvailableCategories(): void {
    const categories = new Set<string>();
    this.additionalOutflowFoodItems.forEach((outflow) => {
      const codigoInsumo = (outflow as any).codigoInsumo;
      if (codigoInsumo && codigoInsumo.length >= 3) {
        categories.add(codigoInsumo.substring(0, 3));
      }
    });
    this.availableCategories = Array.from(categories).sort();
  }
}
